import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Phone, Gift, X, Calendar, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface LPExitIntentPopupProps {
  onClose?: () => void;
  projectName?: string;
}

const LPExitIntentPopup = ({ onClose, projectName = "GPR Infra plots" }: LPExitIntentPopupProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: "", phone: "", visitDate: "" });

  const handleExitIntent = useCallback((e: MouseEvent) => {
    if (e.clientY <= 5 && !hasShown) {
      const shown = sessionStorage.getItem("lpExitIntentShown");
      if (!shown) {
        setIsOpen(true);
        setHasShown(true);
        sessionStorage.setItem("lpExitIntentShown", "true");
      }
    }
  }, [hasShown]);

  // Also trigger on fast scroll up or idle
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let idleTimer: ReturnType<typeof setTimeout>;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDiff = lastScrollY - currentScrollY;
      
      // Fast scroll up (>200px in one frame)
      if (scrollDiff > 200 && !hasShown && currentScrollY > 500) {
        const shown = sessionStorage.getItem("lpExitIntentShown");
        if (!shown) {
          setIsOpen(true);
          setHasShown(true);
          sessionStorage.setItem("lpExitIntentShown", "true");
        }
      }
      lastScrollY = currentScrollY;
    };

    const resetIdleTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        if (!hasShown && window.scrollY > 300) {
          const shown = sessionStorage.getItem("lpExitIntentShown");
          if (!shown) {
            setIsOpen(true);
            setHasShown(true);
            sessionStorage.setItem("lpExitIntentShown", "true");
          }
        }
      }, 30000); // 30 seconds idle
    };

    const timer = setTimeout(() => {
      document.addEventListener("mouseout", handleExitIntent);
      window.addEventListener("scroll", handleScroll);
      window.addEventListener("mousemove", resetIdleTimer);
      window.addEventListener("keydown", resetIdleTimer);
      resetIdleTimer();
    }, 5000);

    return () => {
      clearTimeout(timer);
      clearTimeout(idleTimer);
      document.removeEventListener("mouseout", handleExitIntent);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", resetIdleTimer);
      window.removeEventListener("keydown", resetIdleTimer);
    };
  }, [handleExitIntent, hasShown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error("Please enter name and phone number");
      return;
    }
    if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    setIsSubmitting(true);
    try {
      await supabase.from("leads").insert({
        name: formData.name.trim(),
        phone: formData.phone.replace(/\D/g, ''),
        email: `${formData.phone.replace(/\D/g, '')}@exit.gprinfra.com`,
        source: "Exit Intent Popup - ₹50K Offer",
        status: "new",
        priority: "hot",
        notes: formData.visitDate ? `Preferred visit date: ${formData.visitDate}` : undefined
      });

      toast.success("🎉 Offer claimed! Our team will call you shortly.");
      setIsOpen(false);
      onClose?.();
    } catch (error) {
      console.error("Exit intent lead error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  const whatsappUrl = "https://wa.me/918297978080?text=" + encodeURIComponent(
    `Hi, I saw the ₹50,000 offer on ${projectName}. Please share details.`
  );

  // Get tomorrow's date for min date
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md border-0 p-0 overflow-hidden bg-transparent shadow-none">
        <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl">
          {/* Animated border */}
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 p-[3px] rounded-3xl">
            <div className="absolute inset-[3px] bg-white rounded-[21px]" />
          </div>

          <div className="relative">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 via-orange-500 to-red-600 p-6 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30" />
              
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 text-white hover:bg-white/20 rounded-full"
                onClick={handleClose}
                aria-label="Close popup"
              >
                <X className="h-5 w-5" />
              </Button>

              <div className="relative">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 backdrop-blur mb-3 animate-bounce">
                  <Gift className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-1">
                  రండి! వెళ్ళిపోకండి! 🙏
                </h2>
                <p className="text-white/90 text-sm">
                  Wait! Don't Miss This Exclusive Offer
                </p>
              </div>
            </div>

            {/* Offer Banner */}
            <div className="bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-400 py-3 px-4 text-center">
              <p className="text-lg font-bold text-gray-900 flex items-center justify-center gap-2">
                🎁 Free Site Visit + <span className="text-red-600">₹50,000 OFF</span>
              </p>
              <p className="text-xs text-gray-700">Limited plots available | Offer ends soon</p>
            </div>

            {/* Form */}
            <div className="p-5">
              <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                  type="text"
                  placeholder="మీ పేరు / Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-12 rounded-xl border-2 border-orange-200 focus:border-orange-500 text-base"
                  required
                />
                <Input
                  type="tel"
                  placeholder="మొబైల్ నంబర్ / Mobile Number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="h-12 rounded-xl border-2 border-orange-200 focus:border-orange-500 text-base"
                  pattern="[0-9]{10}"
                  required
                />
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="date"
                    placeholder="Preferred Visit Date"
                    value={formData.visitDate}
                    onChange={(e) => setFormData({ ...formData, visitDate: e.target.value })}
                    className="h-12 rounded-xl border-2 border-gray-200 focus:border-orange-500 text-base pl-10"
                    min={minDate}
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full h-14 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 hover:from-orange-600 hover:via-red-600 hover:to-pink-600 text-white font-bold text-lg rounded-xl shadow-lg"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Phone className="h-5 w-5" />
                      Claim ₹50,000 Offer
                    </span>
                  )}
                </Button>

                <Button
                  type="button"
                  className="w-full h-12 bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#22c55e] hover:to-[#15803d] text-white font-semibold rounded-xl"
                  asChild
                >
                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="h-5 w-5 mr-2" />
                    WhatsApp for Price & Location
                  </a>
                </Button>
              </form>

              <p className="text-xs text-center text-gray-500 mt-3">
                🔒 100% Free | No Spam | Callback in 30 mins
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LPExitIntentPopup;
